import { useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { Input, message, Popover, Spin, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { CasesTable } from "./components/cases-table-layout";
import { ListOfCards } from "./components/list-of-cards";
import { CasesCardsLayout } from "./components/cases-cards-layout";
import { useLanguageTranslator } from "hooks/use-language-translator";
import { SearchOutlined } from "@ant-design/icons";
import {
  CaseAlertIcon,
  CreateCaseIcon,
  CreateColumnIcon,
  CreateNewCaseRed,
  FormLayoutSwitch,
  NotificationIcon,
  PlusIcon,
} from "icons";
import { SwitchCasesList } from "icon-components/switchCasesList";
import { ConfirmationPopUp } from "pages/cases-detail/components/modals";
import { UseApiCallV2, UseLazyApiCallV2 } from "hooks";
import { useCurrentUserContext } from "providers";
import { ConditionalRenderer } from "components";
import { casesListView } from "store/atom";
import styles from "./styles.module.scss";
import { SingleNotification } from "./components/single-notification";
import { SingleNotificationV1 } from "./components/single-notification-v1";
import { getFilterArrOnSearch } from "utils/helpers";
import { AddNewColumnStatus } from "./components/add-new-column-status";
import { AllReminders } from "./components/all-reminders-table";
import { EmptyDataLabel } from "components/empty-data-label";

export const CasesDashboard = () => {
  const { user } = useCurrentUserContext();
  const [userSearch, setUserSearch] = useState("");
  const [tableListView, setTableListView] = useRecoilState(casesListView);
  const [cases, setCases] = useState([]);
  const navigate = useNavigate();
  const [createNewStatusColumn, setCreateNewStatusColumn] = useState(false);
  const [openAllReminders, setOpenAllReminders] = useState(false);
  const { translator } = useLanguageTranslator();
  const casesTableTrans = translator("casesTable");
  const [openCreateCasePopUp, setOpenCreateCasePopUp] = useState(false);
  const [openPendingReminders, setOpenPendingReminders] = useState(false);
  const {
    data: caseTotalStatuses,
    isLoading: caseTotalStatusesLoading,
    refetch: refetchcaseTotalStatuses,
  } = UseApiCallV2({
    url: "/get_status",
    method: "POST",
    paramsData: {
      temp: "all",
    },
  });

  const { data: employees, isLoading: employeesLoading } = UseApiCallV2({
    url: "/get_usernames",
    method: "POST",
    paramsData: {
      email: user?.email,
    },
  });

  const {
    data: columnsStatus,
    isLoading: columnsStatusLoading,
    refetch: refetchColumnsStatus,
  } = UseApiCallV2({
    url: "/get_status",
    method: "POST",
    paramsData: {
      temp: "all",
    },
  });

  const columnsOrder = columnsStatus?.items?.map((item) => item.settingName);

  const {
    data: reminderNotifications,
    isLoading: reminderNotificationsLoading,
    refetch: refetchReminderHistory,
  } = UseApiCallV2({
    url: "/reminder_notification",
    method: "POST",
    paramsData: {
      loggedUserId: user?.email,
    },
  });

  const finalEmployees = useMemo(() => {
    return employees?.users?.map((item) => {
      return {
        label: item?.username,
        value: item?.email,
      };
    });
  }, [employees?.users]);

  const [createNewCase, { isLoading: createNewCaseLoading }] = UseLazyApiCallV2(
    {
      url: "/create_case",
      method: "POST",
      paramsData: {
        email: user?.email,
      },
    },
  );
  const {
    data: casesData,
    isLoading,
    refetch: refetchCases,
  } = UseApiCallV2({
    url: "/get_cases",
    method: "POST",
    paramsData: {
      loggedUser: user?.email,
    },
  });

  useEffect(() => {
    getFilteredData(userSearch);
  }, [userSearch]);

  useEffect(() => {
    setCases(casesData?.items);
  }, [casesData?.items]);

  function onCloseCreateNewStatus() {
    setCreateNewStatusColumn(false);
  }
  function onOpenCreateNewStatus() {
    setCreateNewStatusColumn(true);
  }

  function getFilteredData(userSearch) {
    if (!userSearch) {
      setCases(casesData?.items || []);
      return;
    }

    const search = userSearch.toLowerCase();

    const searchableFields = [
      "nameOfPolicyholder",
      "caseId",
      "createdByName",
      "invoiceNoSmile",
      "customerNoSmile",
    ];

    const filtered = getFilterArrOnSearch({
      searchableFields,
      searchValue: search,
      data: casesData?.items,
    });

    setCases(filtered);
  }

  function handleClickCreateNew() {
    setOpenCreateCasePopUp(true);
  }
  function closeNewCasePopUp() {
    setOpenCreateCasePopUp(false);
  }
  async function handleConfirmNewCase() {
    try {
      const res = await createNewCase();
      if (res.status === "SUCCESS" && res.caseId) {
        closeNewCasePopUp();
        message.success(translator("casesTable.created"));
        navigate(`/cases/${res.caseId}`, {
          state: {
            employees: finalEmployees,
          },
        });
      }
    } catch (error) {
      message.error(translator("somethingWentWrong"));
    }
  }

  function closeAllReminders() {
    setOpenPendingReminders(true);
    setOpenAllReminders(false);
  }

  return (
    <div className={styles.container}>
      <ListOfCards />
      <div className={styles.searchContainer}>
        <div className={styles.textContainer}>
          <h3>{casesTableTrans.overview}</h3>
          <div>{casesTableTrans.desc}</div>
        </div>
        <div className={styles.messageSearch}>
          <div>
            <Input
              placeholder={casesTableTrans.Search}
              onChange={(e) => setUserSearch(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ height: "45px", width: "345px" }}
              // value={filterInput}
              maxLength={40}
            />
          </div>
          <Popover
            open={openPendingReminders}
            onOpenChange={() => setOpenPendingReminders((prev) => !prev)}
            placement="bottom"
            content={
              <Spin spinning={reminderNotificationsLoading}>
                <div className={styles.notificatiosnContainer}>
                  <ConditionalRenderer
                    condition={reminderNotifications?.reminders?.length > 0}
                  >
                    {reminderNotifications?.reminders?.map((item) => (
                      <SingleNotificationV1
                        reminderData={item}
                        key={item.id}
                        refetchReminderHistory={refetchReminderHistory}
                        getReminderDetailLoading={reminderNotificationsLoading}
                        setOpenPendingReminders={setOpenPendingReminders}
                      />
                    ))}
                  </ConditionalRenderer>

                  <ConditionalRenderer
                    condition={
                      reminderNotifications?.reminders?.length < 1 ||
                      !reminderNotifications?.reminders
                    }
                  >
                    <EmptyDataLabel />
                  </ConditionalRenderer>
                </div>
                <div
                  className={styles.seeAll}
                  onClick={() => {
                    setOpenPendingReminders(false);
                    setOpenAllReminders(true);
                  }}
                >
                  {translator("pendingReminders").seeAllNotifications}
                </div>
              </Spin>
            }
            trigger="click"
            overlayInnerStyle={{
              width: "560px",
              maxHeight: "597px",
              minHeight: "150px",
              overflowY: "auto",
            }}
          >
            <Tooltip title={translator("caseDetail.reminder.pendingReminders")}>
              <div
                // style={{ backgroundColor: tableListView ? "white" : "#cd2a3f" }}
                className={styles.switch}
              >
                <CaseAlertIcon height={19} width={24} />

                <ConditionalRenderer
                  condition={reminderNotifications?.reminders?.length > 0}
                >
                  <div className={styles.number}>
                    {reminderNotifications?.reminders?.length}
                  </div>
                </ConditionalRenderer>
                {/* <SwitchCasesList fill={tableListView ? "#cd2a3f" : "white"} /> */}
              </div>
            </Tooltip>
          </Popover>
          <ConditionalRenderer condition={!tableListView}>
            <Tooltip title={translator("createNewStatus")}>
              <div
                className={styles.createNewStatus}
                onClick={onOpenCreateNewStatus}
              >
                <CreateColumnIcon height={18} width={18} />
                {/* <div>Create New Column</div> */}
              </div>
            </Tooltip>
          </ConditionalRenderer>
          <Tooltip title={translator("changeLayout")}>
            <div
              // style={{ backgroundColor: tableListView ? "white" : "#cd2a3f" }}
              className={styles.switch}
              onClick={() => setTableListView((prev) => !prev)}
            >
              <FormLayoutSwitch />
              {/* <SwitchCasesList fill={tableListView ? "#cd2a3f" : "white"} /> */}
            </div>
          </Tooltip>

          <div
            className={styles.createLicenseBtn}
            onClick={handleClickCreateNew}
          >
            <CreateCaseIcon height={20} width={20} />
            <div>{casesTableTrans.createNewCase}</div>
          </div>
        </div>
      </div>
      <ConditionalRenderer condition={tableListView}>
        <CasesTable
          isLoading={isLoading}
          cases={cases}
          employees={employees}
          heightOfTable={`calc(81dvh - 130px)`}
          caseTotalStatuses={caseTotalStatuses?.items}
          refetchCases={refetchCases}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={!tableListView}>
        <CasesCardsLayout
          casesData={cases}
          refetchCases={refetchCases}
          casesLoading={isLoading}
          employees={employees}
          employeesLoading={employeesLoading}
          columnsOrder={columnsOrder}
          columnsStatusData={columnsStatus?.items}
          refetchStatuses={refetchColumnsStatus}
        />
      </ConditionalRenderer>

      <ConfirmationPopUp
        onConfirm={handleConfirmNewCase}
        Icon={CreateNewCaseRed}
        onClose={closeNewCasePopUp}
        open={openCreateCasePopUp}
        title={translator("createNewCase")}
        description={translator("confirmationForCase")}
        loading={createNewCaseLoading}
      />
      <AddNewColumnStatus
        onClose={onCloseCreateNewStatus}
        open={createNewStatusColumn}
        refetchColumnsStatus={refetchColumnsStatus}
        refetchCases={refetchCases}
      />
      <AllReminders
        data={reminderNotifications?.reminders}
        open={openAllReminders}
        handleCloseModal={closeAllReminders}
        employees={employees}
      />
    </div>
  );
};
